(() => {
  if (window.FMMotion) return;

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const SHAPES = 'path, circle, line, polyline, polygon, rect, ellipse';

  const getScrollTop = () => {
    const wrapper = document.querySelector('.page-wrapper');
    return Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
  };

  const scrollListeners = new Set();
  let scrollFrame = 0;

  const flushScroll = () => {
    scrollFrame = 0;
    const top = getScrollTop();
    scrollListeners.forEach((listener) => listener(top));
  };

  const queueScroll = () => {
    if (!scrollFrame) scrollFrame = requestAnimationFrame(flushScroll);
  };

  const bindScrollSources = () => {
    window.addEventListener('scroll', queueScroll, { passive: true });
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && !wrapper.dataset.fmScrollBound) {
      wrapper.dataset.fmScrollBound = 'true';
      wrapper.addEventListener('scroll', queueScroll, { passive: true });
    }
  };

  const onScroll = (listener) => {
    scrollListeners.add(listener);
    listener(getScrollTop());
    return () => scrollListeners.delete(listener);
  };

  const prepareDraw = (element) => {
    if (element.classList.contains('fm-draw-ready')) return;
    element.querySelectorAll('svg').forEach((svg) => {
      svg.querySelectorAll(SHAPES).forEach((shape) => {
        if (shape.closest('clipPath, defs, mask')) return;
        shape.setAttribute('pathLength', '1');
      });
    });
    element.classList.add('fm-draw-ready');
  };

  // Elements revealed with clip-path start fully clipped, and IntersectionObserver treats a
  // fully clipped element (and everything inside it) as not intersecting. Masks are therefore
  // observed through their parent and reveal their nested reveals together with themselves.
  const maskProxies = new Map();

  const revealTree = (element) => {
    element.classList.add('is-in');
    element.querySelectorAll('[data-fm-reveal]:not(.is-in)').forEach((child) => {
      child.classList.add('is-in');
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const target = entry.target;
        const masks = maskProxies.get(target);
        if (masks) {
          masks.forEach(revealTree);
          maskProxies.delete(target);
        }
        if (target.hasAttribute('data-fm-reveal')) target.classList.add('is-in');
        observer.unobserve(target);
      });
    },
    { rootMargin: '0px 0px -6% 0px', threshold: 0.12 }
  );

  const observe = (scope = document) => {
    const elements = scope.querySelectorAll('[data-fm-reveal]:not(.is-in)');
    elements.forEach((element) => {
      if (element.dataset.fmReveal === 'draw') prepareDraw(element);
      if (reducedMotion.matches) {
        element.classList.add('is-in');
        return;
      }
      if (element.parentElement && element.parentElement.closest('[data-fm-reveal^="mask"]:not(.is-in)')) return;
      if (element.dataset.fmReveal.startsWith('mask')) {
        const proxy = element.parentElement || element;
        if (!maskProxies.has(proxy)) maskProxies.set(proxy, new Set());
        maskProxies.get(proxy).add(element);
        observer.observe(proxy);
        return;
      }
      observer.observe(element);
    });
  };

  const setOrigin = (element, event) => {
    const rect = element.getBoundingClientRect();
    element.style.setProperty('--fm-x', `${event.clientX - rect.left}px`);
    element.style.setProperty('--fm-y', `${event.clientY - rect.top}px`);
  };

  document.addEventListener('pointerover', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-fm-fill]') : null;
    if (!target || target.contains(event.relatedTarget)) return;
    setOrigin(target, event);
  });

  document.addEventListener('pointerout', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-fm-fill]') : null;
    if (!target || target.contains(event.relatedTarget)) return;
    setOrigin(target, event);
  });

  let magnetTarget = null;
  let magnetFrame = 0;
  let lastPointer = null;

  const releaseMagnet = () => {
    if (!magnetTarget) return;
    magnetTarget.style.setProperty('--fm-mx', '0px');
    magnetTarget.style.setProperty('--fm-my', '0px');
    magnetTarget = null;
  };

  const updateMagnet = () => {
    magnetFrame = 0;
    if (!lastPointer || !magnetTarget) return;
    const rect = magnetTarget.getBoundingClientRect();
    const strength = parseFloat(magnetTarget.dataset.fmMagnetic || '0.3');
    const dx = lastPointer.x - (rect.left + rect.width / 2);
    const dy = lastPointer.y - (rect.top + rect.height / 2);
    magnetTarget.style.setProperty('--fm-mx', `${dx * strength}px`);
    magnetTarget.style.setProperty('--fm-my', `${dy * strength * 1.2}px`);
  };

  document.addEventListener(
    'pointermove',
    (event) => {
      if (!finePointer.matches || reducedMotion.matches) return;
      const target = event.target instanceof Element ? event.target.closest('[data-fm-magnetic]') : null;
      if (target !== magnetTarget) releaseMagnet();
      if (!target) return;
      magnetTarget = target;
      lastPointer = { x: event.clientX, y: event.clientY };
      if (!magnetFrame) magnetFrame = requestAnimationFrame(updateMagnet);
    },
    { passive: true }
  );

  document.addEventListener('pointerleave', releaseMagnet);

  const parallaxItems = new Set();

  const updateParallax = () => {
    if (reducedMotion.matches || !parallaxItems.size) return;
    const viewport = window.innerHeight;
    parallaxItems.forEach((item) => {
      if (!item.isConnected) {
        parallaxItems.delete(item);
        return;
      }
      const host = item.parentElement || item;
      const rect = host.getBoundingClientRect();
      if (rect.bottom < -100 || rect.top > viewport + 100) return;
      const strength = parseFloat(item.dataset.fmParallax || '0.12');
      // Drift only once the host's top edge has left the viewport, so the strip the
      // image uncovers above itself is always off-screen and no overscale is needed.
      const offset =
        item.dataset.fmParallaxMode === 'drift'
          ? Math.min(Math.max(0, -rect.top), rect.height) * strength
          : (rect.top + rect.height / 2 - viewport / 2) * strength * -1;
      item.style.setProperty('--fm-py', `${offset.toFixed(2)}px`);
    });
  };

  const cssDrift = Boolean(window.CSS && CSS.supports('animation-timeline: view()'));

  const registerParallax = (scope = document) => {
    scope.querySelectorAll('[data-fm-parallax]').forEach((item) => {
      if (cssDrift && item.dataset.fmParallaxMode === 'drift') return;
      parallaxItems.add(item);
    });
    updateParallax();
  };

  const start = () => {
    bindScrollSources();
    observe();
    registerParallax();
    onScroll(updateParallax);
    window.addEventListener('resize', updateParallax, { passive: true });
    root.classList.add('fm-ready');
  };

  const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
  Promise.race([fontsReady, new Promise((resolve) => setTimeout(resolve, 900))]).then(() => {
    root.classList.add('fm-fonts-ready');
  });

  window.FMMotion = { observe, onScroll, getScrollTop, registerParallax, prepareDraw, reducedMotion, finePointer };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    observe(event.target);
    registerParallax(event.target);
  });
})();
